import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Resolves the active program slug for outgoing requests.
// Priority: ?program= → localStorage → hostname subdomain → 'freedom'.
const resolveActiveProgramSlug = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('program');
    if (fromQuery) return fromQuery.toLowerCase();

    const stored = localStorage.getItem('active_program');
    if (stored) return stored.toLowerCase();

    const host = window.location.hostname;
    if (host) {
      const parts = host.split('.');
      // Only treat first segment as program slug when it's a real subdomain
      // (e.g. freedom.lifyup.com.br → 4 parts). Apex (lifyup.com.br → 3) uses default.
      if (parts.length >= 4) {
        const sub = parts[0];
        if (sub && !['www', 'api', 'admin', 'localhost', '127'].includes(sub)) {
          return sub.toLowerCase();
        }
      }
    }
  } catch (_) {
    // window/localStorage may be unavailable (SSR/tests) — fall through
  }
  return 'freedom';
};

// Request interceptor: auth token + X-Program header
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['X-Program'] = resolveActiveProgramSlug();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    if (status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');

      if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login';
      }
    }

    if (status === 403) {
      const body = error.response?.data || {};
      const message = body.message || 'Acesso negado: funcionalidade não disponível no seu plano.';
      // Dispara evento customizado para que componentes possam exibir o feedback correto
      window.dispatchEvent(
        new CustomEvent('api:forbidden', {
          detail: { message, feature: body.feature, upgrade_required: body.upgrade_required },
        })
      );
    }

    return Promise.reject(error);
  }
);

// Programs API (public)
export const programsAPI = {
  list: () => api.get('/programs'),
  getBySlug: (slug) => api.get(`/programs/by-slug/${slug}`),
};

// Programs API (admin)
export const programsAdminAPI = {
  list: () => api.get('/admin/programs'),
  getById: (id) => api.get(`/admin/programs/${id}`),
  create: (data) => api.post('/admin/programs', data),
  update: (id, data) => api.put(`/admin/programs/${id}`, data),
  toggleStatus: (id, is_active) => api.patch(`/admin/programs/${id}/status`, { is_active }),
  delete: (id) => api.delete(`/admin/programs/${id}`),
  updateBlocker: (id, data) => api.put(`/admin/programs/${id}/blocker`, data),
  listEmailTemplates: (id) => api.get(`/admin/programs/${id}/email-templates`),
  upsertEmailTemplate: (id, key, data) =>
    api.put(`/admin/programs/${id}/email-templates/${key}`, data),
  deleteEmailTemplate: (id, key) =>
    api.delete(`/admin/programs/${id}/email-templates/${key}`),
  updateFcmCredentials: (id, credentials) =>
    api.put(`/admin/programs/${id}/fcm-credentials`, { credentials }),
};

// Admin Auth API
export const adminAuthAPI = {
  login: (email, password) => api.post('/admin/login', { email, password }),
  getProfile: () => api.get('/admin/profile'),
};

// Dashboard API
export const dashboardAPI = {
  getOverview: () => api.get('/admin/dashboard/overview'),
  getRecentActivity: (limit = 20) => api.get(`/admin/dashboard/activity?limit=${limit}`),
};

// Users API
export const usersAPI = {
  getAll: (params) => api.get('/admin/users', { params }),
  getById: (id) => api.get(`/admin/users/${id}`),
  create: (data) => api.post('/admin/users', data),
  update: (id, data) => api.put(`/admin/users/${id}`, data),
  toggleStatus: (id, is_active) => api.patch(`/admin/users/${id}/status`, { is_active }),
  delete: (id) => api.delete(`/admin/users/${id}`),
  getStats: (id, days = 30) => api.get(`/admin/users/${id}/stats?days=${days}`),
};

// Subscriptions API — public (no auth required)
export const publicSubscriptionsAPI = {
  getPlans: () => api.get('/subscriptions/plans'),
};

// Subscriptions API
export const subscriptionsAPI = {
  getPlans: () => api.get('/admin/subscriptions/plans'),
  createPlan: (data) => api.post('/admin/subscriptions/plans', data),
  updatePlan: (id, data) => api.put(`/admin/subscriptions/plans/${id}`, data),
  getSubscriptions: (params) => api.get('/admin/subscriptions', { params }),
  getSubscriptionById: (id) => api.get(`/admin/subscriptions/${id}`),
  cancelSubscription: (id, cancel_at_period_end = true) =>
    api.post(`/admin/subscriptions/${id}/cancel`, { cancel_at_period_end }),
  getPayments: (params) => api.get('/admin/payments', { params }),
  getRevenueStats: (period = '30') => api.get(`/admin/revenue/stats?period=${period}`),
};

// Notifications API
export const notificationsAPI = {
  sendToUser: (userId, data) => api.post(`/notifications/send/user/${userId}`, data),
  sendToAll: (data) => api.post('/notifications/send/all', data),
  sendToTopic: (data) => api.post('/notifications/send/topic', data),
  getLogs: (params) => api.get('/notifications/logs', { params }),
};

// Branding API
export const brandingAPI = {
  get: () => api.get('/admin/branding'),
  update: (data) => api.post('/admin/branding/update', data),
  uploadLogo: (formData) => api.post('/admin/branding/upload-logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  uploadIcon: (formData) => api.post('/admin/branding/upload-icon', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  uploadBackground: (page, formData) => api.post(`/admin/branding/upload-background/${page}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  removeBackground: (page) => api.delete(`/admin/branding/remove-background/${page}`),
  reset: () => api.post('/admin/branding/reset'),
};

// Analytics API
export const analyticsAPI = {
  getPlatformStats: (period = '30') => api.get(`/admin/analytics/platform?period=${period}`),
  getUserEngagement: (period = '30') => api.get(`/admin/analytics/engagement?period=${period}`),
  getFeatureUsage: (period = '30') => api.get(`/admin/analytics/features?period=${period}`),
  getContentStats: () => api.get('/admin/analytics/content'),
  getRecoveryStats: () => api.get('/admin/analytics/recovery'),
  exportData: (type, format = 'json') =>
    api.get(`/admin/analytics/export?type=${type}&format=${format}`),
};

// Settings API
export const settingsAPI = {
  getAll: () => api.get('/admin/settings'),
  updateMultiple: (settings) => api.post('/admin/settings/batch', { settings }),
  update: (key, value, description) => api.put(`/admin/settings/${key}`, { value, description }),
};

// ─── Blog / CMS Admin ──────────────────────────────────────────────────────
export const blogAdminAPI = {
  listPosts:    (params) => api.get('/admin/blog/posts', { params }),
  getPost:      (id) => api.get(`/admin/blog/posts/${id}`),
  createPost:   (data) => api.post('/admin/blog/posts', data),
  updatePost:   (id, data) => api.put(`/admin/blog/posts/${id}`, data),
  deletePost:   (id) => api.delete(`/admin/blog/posts/${id}`),

  listCategories:  () => api.get('/admin/blog/categories'),
  createCategory:  (data) => api.post('/admin/blog/categories', data),
  updateCategory:  (id, data) => api.put(`/admin/blog/categories/${id}`, data),
  deleteCategory:  (id) => api.delete(`/admin/blog/categories/${id}`),

  listTags:    () => api.get('/admin/blog/tags'),
  createTag:   (data) => api.post('/admin/blog/tags', data),
  updateTag:   (id, data) => api.put(`/admin/blog/tags/${id}`, data),
  deleteTag:   (id) => api.delete(`/admin/blog/tags/${id}`),

  listAuthors:  () => api.get('/admin/blog/authors'),
  createAuthor: (data) => api.post('/admin/blog/authors', data),
  updateAuthor: (id, data) => api.put(`/admin/blog/authors/${id}`, data),
  deleteAuthor: (id) => api.delete(`/admin/blog/authors/${id}`),
};

// ─── SEO Admin ─────────────────────────────────────────────────────────────
export const seoAdminAPI = {
  getSettings:    () => api.get('/admin/seo/settings'),
  updateSettings: (data) => api.put('/admin/seo/settings', data),
  getProgramSeo:    (programId) => api.get(`/admin/seo/programs/${programId}`),
  upsertProgramSeo: (programId, data) => api.put(`/admin/seo/programs/${programId}`, data),
};

// ─── Blog (public) ─────────────────────────────────────────────────────────
export const blogAPI = {
  listPosts:        (params) => api.get('/blog/posts', { params }),
  getPostBySlug:    (slug) => api.get(`/blog/posts/${slug}`),
  listCategories:   () => api.get('/blog/categories'),
  getCategoryBySlug:(slug) => api.get(`/blog/categories/${slug}`),
  listTags:         () => api.get('/blog/tags'),
  getTagBySlug:     (slug) => api.get(`/blog/tags/${slug}`),
  getAuthorBySlug:  (slug) => api.get(`/blog/authors/${slug}`),
};

// ─── SEO (public) ──────────────────────────────────────────────────────────
export const seoAPI = {
  getSettings:   () => api.get('/seo/settings'),
  getProgramSeo: (slug) => api.get(`/seo/programs/${slug}`),
};

export default api;
